/*
 * Voyado card quick-add filler — extracted from voyado-card-renderer.js to keep
 * each file near the 150-line JS cap (CONVENTIONS). Mirrors
 * snippets/product-card-quick-add.liquid: the trigger opens the size drawer and
 * each size pill dispatches product-card:add when clicked (wired in
 * voyado-card-behaviour.js).
 *
 * Default-variant products (a single variant labelled "Default", no real sizes)
 * render a normal quick-add drawer with one "One Size" pill — in stock it adds
 * to bag, out of stock it links to the PDP. Mirrors product-card.liquid.
 *
 * Pills carry data-variant-id (for the add) + data-voyado-ticket-value (for the
 * notify, copied to the card root at click — never data-voyado-ticket here,
 * which would double-fire a click notify via voyado-client.js). The direct-add
 * trigger carries the same two attributes.
 *
 * Exposes window.BroraVoyadoCardQuickAdd.fill(root, product, { tmpl, slot,
 * variantId }) so the renderer passes in its shared cloner helpers.
 */
;(function () {

  // Ajax routes need the market root, or Shopify answers in the default market. routes.root ends in '/'.
  function shopRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  'use strict';

  // A single variant labelled "Default" is Shopify's default variant surfaced by
  // Voyado — the product has no real size options to choose from.
  function isDefaultOnly(variants) {
    if (variants.length !== 1) return false;
    const v = variants[0];
    return v.size === 'Default' || v.label === 'Default';
  }

  // Product handle from the Voyado product link (/products/<handle>[?...]). Stamped on the card so the
  // quick-add gate (voyado-card-behaviour.js) can fetch /products/<handle>.js at click time — the only
  // place the client-side pre-order signal (selling_plan_allocations) is available for a Voyado card.
  function handleOf(product) {
    const m = ((product && product.link) || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  // Per-handle cache of pre-order variant ids (strings). Populated by revealPreorderPills (once per card, on
  // idle) so the quick-add gate decides synchronously without a second fetch. Shared across cards of the same
  // product for the page session.
  var preorderCache = new Map();

  // Set of pre-order variant ids (strings) from a product.js payload — a variant is pre-order when it carries
  // a selling-plan allocation (Notify Me attaches the plan to exactly the pre-order variants).
  function preorderSet(product) {
    var set = new Set();
    (((product || {}).variants) || []).forEach(function (v) {
      if (window.BroraPreorderMarket ? window.BroraPreorderMarket.anyAllowed(v.selling_plan_allocations) : (v.selling_plan_allocations || []).length > 0) set.add(String(v.id));
    });
    return set;
  }

  function fill(root, product, helpers) {
    const tmpl = helpers.tmpl;
    const slot = helpers.slot;
    const variantId = helpers.variantId;
    const safeUrl = helpers.safeUrl;
    // The out-of-stock pill navigates, so its href needs the market root too.
    const withMarket = helpers.withMarket || function (p) { return p; };
    const gallery = root.querySelector('[data-voyado-card-gallery]');
    const variants = (product.variants || []).filter(variantId);
    if (!gallery || variants.length === 0) return;

    const handle = handleOf(product);
    if (handle) root.setAttribute('data-product-handle', handle);

    // Default-variant → single "One Size" pill in the normal drawer (see file header).
    const defaultOnly = isDefaultOnly(variants);

    const qa = tmpl('card-quick-add');
    if (!qa) return;
    const trigger = slot(qa, 'trigger');
    const drawer = slot(qa, 'drawer');
    if (trigger) trigger.setAttribute('aria-label', 'Quick add ' + (product.title || ''));
    variants.forEach(function (v) {
      if (!drawer) return;
      const id = variantId(v);
      const label = defaultOnly ? 'One Size' : (v.label || v.size || '');
      if (v.inStock) {
        const pill = tmpl('card-size');
        if (!pill) return;
        pill.setAttribute('data-variant-id', id);
        pill.setAttribute('data-voyado-ticket-value', v.ticket || '');
        const input = slot(pill, 'input');
        if (input) { input.name = 'voyado-size-' + product.key; input.value = id; }
        const text = slot(pill, 'text');
        if (text) text.textContent = label;
        // Available — drop the out-of-stock icon + strikethrough line.
        const icon = slot(pill, 'oos-icon'); if (icon) icon.remove();
        const line = pill.querySelector('.action-size__line'); if (line) line.remove();
        drawer.appendChild(pill);
      } else {
        // Out of stock — a native link to the variant's PDP (no add-to-bag),
        // mirroring product-card-quick-add.liquid. No data-quick-add-size, so the
        // behaviour file ignores it and the link navigates natively.
        const pill = tmpl('card-size-oos');
        if (!pill) return;
        const href = safeUrl ? withMarket(safeUrl(v.link || '')) : '';
        if (href) pill.setAttribute('href', href);
        const text = slot(pill, 'text');
        if (text) text.textContent = label;
        drawer.appendChild(pill);
      }
    });
    gallery.appendChild(qa);
    revealPreorderPills(root, handle, tmpl);
  }

  // Pre-order gate for a Voyado quick-add. Pre-order can only be completed on the PDP, so a pre-order variant
  // must route there instead of adding. The pre-order variant set comes from preorderCache (populated by
  // revealPreorderPills as the card nears the viewport) — a synchronous decision, no network. Only when the
  // set is not yet known does it fetch /products/<handle>.js once, cache it, then decide.
  // Pre-order → navigate to the PDP on that variant (do not add); otherwise, or on any fetch error, fall
  // through to onProceed (the normal add), so a transient failure never blocks an ordinary purchase.
  function gatePreorder(handle, variantId, onProceed) {
    if (!handle || !variantId) { onProceed(); return; }
    var route = function (set) {
      if (set.has(String(variantId))) {
        var pdp = '/products/' + handle + '?variant=' + variantId;
        window.location.href = window.voyadoWithMarket ? window.voyadoWithMarket(pdp) : pdp;
      }
      else onProceed();
    };
    var cached = preorderCache.get(handle);
    if (cached) { route(cached); return; }
    var ctrl = new AbortController();
    var t = setTimeout(function () { ctrl.abort(); }, 8000);
    fetch(shopRoot() + 'products/' + handle + '.js', { signal: ctrl.signal })
      .then(function (r) { if (!r.ok) throw new Error('fetch_failed'); return r.json(); })
      .then(function (d) {
        clearTimeout(t);
        var set = preorderSet(d);
        preorderCache.set(handle, set);
        route(set);
      })
      .catch(function () { clearTimeout(t); onProceed(); });
  }

  // Reveals variant-level pre-order on Voyado cards. The Elevate feed carries only inventory-based
  // stock (v.custom is empty — no per-variant pre-order/selling-plan signal), so a continue-selling pre-order
  // variant renders as an out-of-stock pill (strikethrough). Called when the size selector is built (fill),
  // so the pill reads correctly before the drawer is ever opened. We fetch /products/<handle>.js once per
  // card, deferred until the card nears the viewport (whenNearViewport), and un-style the OOS pills whose
  // variant carries a selling plan — they already link to the PDP, so only the visual changes: they read as
  // an ordinary, orderable size that still routes to the PDP. Fail-safe: on any error the pills keep their
  // OOS look + link. Runs only when the card actually has OOS pills (no fetch for fully in-stock products).
  // When any pre-order variant is found, a "Pre-order" badge is added to the card (addPreorderBadge).
  function revealPreorderPills(root, handle, tmpl) {
    if (!root || !handle || root.dataset.voyadoPreorderChecked) return;
    root.dataset.voyadoPreorderChecked = '1';
    var oos = root.querySelectorAll('.action-size--unavailable');
    // No OOS pills → no pre-order variants (they render as OOS pills). Cache the empty set so the gate can
    // decide synchronously without a fetch.
    if (!oos.length) { preorderCache.set(handle, new Set()); return; }
    var releaseStockBadge = holdStockBadge(root);
    var run = function () {
      var controller = new AbortController();
      var timer = setTimeout(function () { controller.abort(); }, STOCK_BADGE_HOLD_MS);
      fetch(shopRoot() + 'products/' + handle + '.js', { signal: controller.signal })
        .then(function (r) { clearTimeout(timer); return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p) { releaseStockBadge(); return; }
          var pre = preorderSet(p);
          preorderCache.set(handle, pre);
          oos.forEach(function (pill) {
            var m = (pill.getAttribute('href') || '').match(/variant=(\d+)/);
            if (m && pre.has(m[1])) {
              pill.classList.remove('action-size--unavailable');
              pill.setAttribute('data-preorder', '');
              var line = pill.querySelector('.action-size__line');
              if (line) line.remove();
              var icon = pill.querySelector('.action-size__icon');
              if (icon) icon.remove();
              var sr = pill.querySelector('.sr-only');
              if (sr) sr.textContent = ' — view on product page';
            }
          });
          if (pre.size) addPreorderBadge(root, tmpl);
          else releaseStockBadge();
        })
        .catch(function () { clearTimeout(timer); releaseStockBadge(); });
    };
    whenNearViewport(root, run);
  }

  // Each card asks as it nears the viewport, so the cards on screen do not queue behind cards further
  // down the page. PREFETCH_MARGIN gives the lookup a head start on the card being seen. One observer
  // serves every card; without IntersectionObserver, every card asks on idle.
  var PREFETCH_MARGIN = '600px';
  var viewportPending = new WeakMap();
  var viewportObserver = null;

  function whenNearViewport(el, cb) {
    if (typeof IntersectionObserver !== 'function') {
      (window.requestIdleCallback || function (fn) { setTimeout(fn, 200); })(cb);
      return;
    }
    if (!viewportObserver) {
      viewportObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          viewportObserver.unobserve(entry.target);
          var pending = viewportPending.get(entry.target);
          if (pending) { viewportPending.delete(entry.target); pending(); }
        });
      }, { rootMargin: PREFETCH_MARGIN });
    }
    viewportPending.set(el, cb);
    viewportObserver.observe(el);
  }

  // The stock badge and the pre-order badge answer the same question, and the feed knows only the stock
  // half, so the badge is held until the lookup answers. The container is absolutely positioned, so the
  // hold shifts nothing. The hold ends on the lookup — settled, failed, or aborted at STOCK_BADGE_HOLD_MS.
  var STOCK_BADGE_HOLD_MS = 3000;

  function holdStockBadge(root) {
    var container = root.querySelector('[data-slot="badges"]');
    var held = container
      ? Array.prototype.slice.call(container.children).filter(function (b) {
          var kind = b.getAttribute('data-card-badge');
          return kind === 'out-of-stock' || kind === 'low-stock';
        })
      : [];
    if (!held.length) return function () {};
    held.forEach(function (b) { b.classList.add('hidden'); });
    var released = false;
    return function () {
      if (released) return;
      released = true;
      held.forEach(function (b) { b.classList.remove('hidden'); });
    };
  }

  // Adds the "Pre-order" badge to a Voyado card once a pre-order variant is confirmed. Mirrors the Liquid
  // card badge precedence (product-card-badges.liquid): pre-order supersedes the OOS / Low-stock stock
  // badges; manual badges (Bestseller etc.) are kept. Clones the shared card-badge template so it matches
  // fillBadges. Idempotent via the data-preorder-badge marker.
  function addPreorderBadge(root, tmpl) {
    var container = root.querySelector('[data-slot="badges"]');
    if (!container || !tmpl || container.querySelector('[data-preorder-badge]')) return;
    var node = tmpl('card-badge');
    if (!node) return;
    Array.prototype.slice.call(container.children).forEach(function (b) {
      var kind = b.getAttribute('data-card-badge');
      if (kind === 'out-of-stock' || kind === 'low-stock') b.remove();
    });
    node.textContent = 'Pre-order';
    node.setAttribute('data-card-badge', 'pre-order');
    node.setAttribute('data-preorder-badge', '');
    container.insertBefore(node, container.firstChild);
  }

  window.BroraVoyadoCardQuickAdd = { fill: fill, gatePreorder: gatePreorder, revealPreorderPills: revealPreorderPills };
})();
